import { useCallback, useEffect, useMemo } from 'react'
import { cn } from '@mriqbox/ui-kit'
import { useElementSize } from '@/hooks/useElementSize'
import { averageAspect, computeMosaicLayout } from '@/lib/layout'
import { StreamTile, type StageStream, type StreamTileProps } from '@/components/StreamTile'
import type { PeerConnectionState } from '@/lib/transport'
import type { StreamMeasure } from '@/lib/streamQuality'
import type { ReceivePreference } from '@/lib/receiveQuality'

/** O que cada tile mediu no proprio `<video>`, por id de tile. */
export type StreamMeasures = Record<string, StreamMeasure>

export type ViewMode = 'mosaico' | 'destaque'

export interface StageProps {
    streams: StageStream[]
    mode: ViewMode
    /** Tela em destaque. Se sumir da sala, o palco escolhe a primeira. */
    focusedId: string | null
    onFocusChange: (id: string | null) => void
    /** Mostrado quando ninguem esta transmitindo. */
    empty: React.ReactNode
    /** Manda uma transmissao sozinha pra janela flutuante. */
    onPip?: (id: string) => void
    /** Tira uma transmissao do palco, so pra quem assiste. */
    onHide?: (id: string) => void
    /** Qual esta na janela flutuante agora. */
    pipId?: string | null
    /**
     * Preferencia de som por transmissao, lembrada pela pagina.
     *
     * Fica fora do tile porque ele e remontado quando o id do participante
     * muda: guardada la dentro, a escolha se perdia a cada reconexao.
     */
    audioOn?: Record<string, boolean>
    volumes?: Record<string, number>
    onAudioChange?: (id: string, audioOn: boolean) => void
    onTileVolumeChange?: (id: string, volume: number) => void
    /** Fone silenciado: cala tambem o som das transmissoes. */
    deafened?: boolean
    /** Ids de quem esta falando agora, pra marcar o tile no palco. */
    speaking?: Set<string>
    /**
     * Conexao de midia por peer (mesh ou SFU, mesmo vocabulario). Opcional pra
     * nao quebrar quem monta o palco sem essa informacao — cada tile decide
     * sozinho o que mostrar quando falta.
     */
    peerStates?: Record<string, PeerConnectionState>
    /**
     * Tamanho medido de cada transmissao. Mora fora do palco porque a lista de
     * participantes mostra a mesma etiqueta — guardar a medida em dois lugares
     * daria duas verdades sobre o mesmo video.
     */
    measures?: StreamMeasures
    onMeasure?: (id: string, measure: StreamMeasure) => void
    /** Bits por segundo por id de trilha de video, medidos pelo transporte. */
    bitrates?: Record<string, number>
    /** O servidor liga a medicao de cadencia e banda (`STREAM_STATS`). */
    statsEnabled?: boolean
    receivePreference?: ReceivePreference
    onReceiveLimit?: StreamTileProps['onReceiveLimit']
    className?: string
}

const GAP = 12

/**
 * Palco da sala. Dois jeitos de assistir:
 *
 * - **mosaico**: todas as telas no mesmo tamanho, na grade que faz cada uma
 *   ocupar a maior area possivel (com uma tela so, ela toma o palco inteiro).
 * - **destaque**: uma tela grande + as outras numa tira de miniaturas.
 */
export function Stage({
    streams,
    mode,
    focusedId,
    onFocusChange,
    empty,
    onPip,
    onHide,
    pipId,
    audioOn,
    volumes,
    onAudioChange,
    onTileVolumeChange,
    deafened = false,
    speaking,
    peerStates,
    measures,
    onMeasure,
    bitrates,
    statsEnabled = false,
    receivePreference,
    onReceiveLimit,
    className
}: StageProps) {
    const { ref, size } = useElementSize<HTMLDivElement>()

    /** Banda daquele tile — a ponte e a trilha de video da stream. */
    const bitrateOf = useCallback((entry: StageStream) => {
        const trackId = entry.stream?.getVideoTracks?.()[0]?.id
        return trackId ? bitrates?.[trackId] : undefined
    }, [bitrates])

    const focused = useMemo(
        () => streams.find((entry) => entry.id === focusedId) ?? streams[0] ?? null,
        [streams, focusedId]
    )

    // Quem estava em destaque parou de transmitir: cai pra proxima tela sozinho.
    useEffect(() => {
        if (mode !== 'destaque') return
        if (focusedId && streams.some((entry) => entry.id === focusedId)) return
        onFocusChange(streams[0]?.id ?? null)
    }, [mode, focusedId, streams, onFocusChange])

    /**
     * Decide pelo MODO, nao pelo `focusedId`: ele continua guardado quando a
     * pessoa volta pro mosaico pela barra de cima ou pela tecla M, e ai o
     * primeiro clique em "Destacar" naquela tela so limpava o que sobrou.
     */
    const toggleFocus = useCallback((id: string) => {
        onFocusChange(mode === 'destaque' ? null : id)
    }, [mode, onFocusChange])

    if (streams.length === 0) {
        return (
            <div ref={ref} className={cn('flex h-full w-full items-center justify-center', className)}>
                {empty}
            </div>
        )
    }

    if (mode === 'destaque' && focused) {
        const others = streams.filter((entry) => entry.id !== focused.id)
        return (
            <div ref={ref} className={cn('flex h-full w-full flex-col gap-3', className)} data-testid="stage-destaque">
                <div className="min-h-0 flex-1">
                    <StreamTile
                        entry={focused}
                        focused
                        className="h-full w-full"
                        onToggleFocus={toggleFocus}
                        onPip={onPip}
                        onHide={onHide}
                        audioOn={audioOn?.[focused.id] ?? false}
                        volume={volumes?.[focused.id] ?? 1}
                        onAudioChange={onAudioChange}
                        onVolumeChange={onTileVolumeChange}
                        isPip={pipId === focused.id}
                        onMeasure={onMeasure}
                        bitrate={bitrateOf(focused)}
                        statsEnabled={statsEnabled}
                        deafened={deafened}
                        speaking={speaking?.has(focused.peerId)}
                        peerState={peerStates?.[focused.peerId]}
                        receivePreference={receivePreference}
                        onReceiveLimit={onReceiveLimit}
                    />
                </div>
                {others.length > 0 && (
                    <div className="flex h-24 shrink-0 gap-3 overflow-x-auto pb-1" data-testid="stage-filmstrip">
                        {others.map((entry) => (
                            <button
                                key={entry.id}
                                type="button"
                                onClick={() => onFocusChange(entry.id)}
                                className="h-full w-40 shrink-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                title={`Destacar a tela de ${entry.nickname}`}
                            >
                                <StreamTile
                                    entry={entry}
                                    compact
                                    className="h-full w-full"
                                    onMeasure={onMeasure}
                                    statsEnabled={statsEnabled}
                                    deafened={deafened}
                                    speaking={speaking?.has(entry.peerId)}
                                    peerState={peerStates?.[entry.peerId]}
                                    receivePreference={receivePreference}
                                    onReceiveLimit={onReceiveLimit}
                                />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    const aspect = averageAspect(streams.map((entry) => {
        const medida = measures?.[entry.id]
        return medida && medida.height > 0 ? medida.width / medida.height : 16 / 9
    }))
    const layout = computeMosaicLayout({
        count: streams.length,
        width: size.width,
        height: size.height,
        aspect,
        gap: GAP
    })

    /**
     * Largura exata de uma linha cheia da grade calculada.
     *
     * A quebra de linha fica amarrada a ESTA medida em vez de ficar por conta
     * do palco inteiro: com a linha medindo exatamente `cols` tiles, o flex nao
     * tem como encaixar um a mais nem espaco sobrando pra empurrar o ultimo pra
     * baixo. O `flex-wrap` continua sendo quem quebra — e o que mantem a ultima
     * linha incompleta centralizada, que uma grade de colunas fixas perderia.
     */
    const rowWidth = layout.cols > 0
        ? layout.cols * layout.tileWidth + GAP * (layout.cols - 1)
        : 0

    return (
        // `overflow-hidden` e o cinto de seguranca: se ainda assim sobrar meio
        // pixel, ele fica dentro do palco em vez de vazar por cima da dock.
        <div ref={ref} className={cn('h-full w-full overflow-hidden', className)} data-testid="stage-mosaico">
            <div
                className="mx-auto flex h-full flex-wrap content-center items-center justify-center"
                style={{ gap: GAP, width: rowWidth > 0 ? rowWidth : '100%' }}
            >
                {streams.map((entry) => (
                    <StreamTile
                        key={entry.id}
                        entry={entry}
                        focused={streams.length === 1}
                        deafened={deafened}
                        speaking={speaking?.has(entry.peerId)}
                        style={layout.tileWidth > 0
                            ? { width: layout.tileWidth, height: layout.tileHeight }
                            : { width: '100%', height: '100%' }}
                        // Com uma tela so, destaque e mosaico sao a mesma
                        // imagem — o botao trocaria o modo sem mudar nada.
                        onToggleFocus={streams.length > 1 ? toggleFocus : undefined}
                        onPip={onPip}
                        onHide={onHide}
                        audioOn={audioOn?.[entry.id] ?? false}
                        volume={volumes?.[entry.id] ?? 1}
                        onAudioChange={onAudioChange}
                        onVolumeChange={onTileVolumeChange}
                        isPip={pipId === entry.id}
                        onMeasure={onMeasure}
                        bitrate={bitrateOf(entry)}
                        statsEnabled={statsEnabled}
                        peerState={peerStates?.[entry.peerId]}
                        receivePreference={receivePreference}
                        onReceiveLimit={onReceiveLimit}
                    />
                ))}
            </div>
        </div>
    )
}
